import { Dispatch, SetStateAction, useCallback, useState } from "react";

/**
 * Drop-in replacement for `useState` whose value survives navigation and
 * reloads via localStorage.
 *
 * Use it for list-page "view state" (filters, sort, column visibility, page
 * size) that users expect to still be there when they come back from a detail
 * page. Namespace keys as `view:<page>:<field>`.
 *
 * Pages that already own a persisted zustand store (patients, calls) should
 * keep their state there instead.
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? defaultValue : (JSON.parse(raw) as T);
    } catch {
      return defaultValue;
    }
  });

  const setAndPersist = useCallback<Dispatch<SetStateAction<T>>>(
    (update) => {
      setValue((prev) => {
        const next =
          typeof update === "function" ? (update as (p: T) => T)(prev) : update;
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // Storage unavailable (private mode, quota): keep in-memory only.
        }
        return next;
      });
    },
    [key]
  );

  return [value, setAndPersist];
}
